/**
 * Client for the credit-balance + code-redemption control-plane reads/writes
 * (#44, FR-12.1/12.4/12.5). Credits are the billing currency — 1 credit = 1
 * paid-path game (dynamic generation or auto-host voice); only the host spends,
 * players join free. There is no purchase/checkout in v1: the only in-app way to
 * add credits is to redeem a code (FR-9.5, one goal = one action).
 *
 * Identity is proven by the signed guest token (FR-9.3): the balance read sends it
 * in the `x-guest-token` header (a GET with no body, like `GET /api/account/credits`
 * on the server), and the redeem POST carries it in the JSON envelope. The response
 * shapes mirror the server's `CreditBalance` (`{ free, paid, total }`) so the client
 * can't drift; the two buckets are shown to the user as a single `total`.
 */

import { SERVER_HTTP_URL } from '../config';
import { ApiError } from './api-error';

/** A player's credit balance — two server-side buckets shown as one `total`. */
export interface CreditBalance {
  /** One-time signup free grant (never refreshed, #171). */
  free: number;
  /** Durable purchased/redeemed/comped credits. */
  paid: number;
  /** `free + paid` — the single number the UI shows. */
  total: number;
}

/** The outcome of a successful redemption: what was granted + the new balance. */
export interface RedeemResult {
  granted: { free?: number; paid?: number };
  balance: CreditBalance;
}

/**
 * Friendly, non-technical copy per redeem failure status (FR-9.5). The server
 * distinguishes unknown (`404`), expired (`410`), exhausted / already-redeemed
 * (`409`), and a missing code (`422`); anything else falls back to a generic retry.
 */
const REDEEM_FAILURE_MESSAGES: Readonly<Record<number, string>> = {
  404: 'We don’t recognize that code.',
  410: 'That code has expired.',
  409: 'That code has already been used up.',
  422: 'Enter a code to redeem.',
};

/**
 * Fetch the caller's current credit balance. The server seeds the one-time signup
 * free-grant on first read (#171) and fails open (a ledger blip returns a zero balance,
 * never an error), so a non-2xx here is a genuine transport/identity failure.
 */
export async function fetchCreditBalance(guestToken: string): Promise<CreditBalance> {
  const res = await fetch(`${SERVER_HTTP_URL}/api/account/credits`, {
    headers: { 'x-guest-token': guestToken },
  });
  if (!res.ok) {
    throw new ApiError(`Could not load your credit balance (${res.status})`, res.status);
  }
  return (await res.json()) as CreditBalance;
}

/**
 * Redeem a discount code for credits. On success returns what was granted plus the
 * updated balance so the caller can refresh what it shows. On a rejected code throws
 * an {@link ApiError} carrying the status and a friendly message the UI can render
 * as-is.
 */
export async function redeemCode(
  guestToken: string,
  code: string,
): Promise<RedeemResult> {
  const res = await fetch(`${SERVER_HTTP_URL}/api/account/redeem`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ guestToken, code }),
  });
  if (!res.ok) {
    const message =
      REDEEM_FAILURE_MESSAGES[res.status] ??
      'Could not redeem that code. Please try again.';
    throw new ApiError(message, res.status);
  }
  const body = (await res.json()) as {
    ok: boolean;
    granted: { free?: number; paid?: number };
    balance: CreditBalance;
  };
  return { granted: body.granted, balance: body.balance };
}
